import { Box } from '@mui/material';
import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo
} from 'react';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';
import { searchProgressSx } from 'src/content/keywordResearch/keywordResearchCSS';
import HelpIcon from '@mui/icons-material/Help';

// 1. Define Context for state sharing between composed components
interface ITitleTooltipContextProps {
  title?: ReactNode;
  tooltip?: ReactNode;
}

const TitleTooltipContext = createContext<
  ITitleTooltipContextProps | undefined
>(undefined);

export function useTitleTooltipContext() {
  const context = useContext(TitleTooltipContext);
  if (!context) {
    throw new Error(
      'TitleTooltip subcomponents must be used within a <TitleTooltip /> component.'
    );
  }
  return context;
}

// 2. Define Props Interfaces
export interface ITitleTooltipProps extends PropsWithChildren {
  title?: ReactNode;
  tooltip?: ReactNode;
}

export interface ITitleTooltipTextProps extends PropsWithChildren {
  title?: ReactNode;
  className?: string;
}

export interface ITitleTooltipTooltipProps extends PropsWithChildren {
  tooltip?: ReactNode;
  className?: string;
}

// 3. Main/Root Component
/**
 * TitleTooltip Component
 * 
 * A flexible, accessible component that pairs a title/label with an optional help tooltip.
 * Implements the component composition pattern with a shared React context.
 * 
 * @example
 * // Option 1: Legacy / Simple Usage (Direct Props)
 * <TitleTooltip title="Keyword Difficulty" tooltip="Shows how difficult this keyword is to rank for." />
 * 
 * @example
 * // Option 2: Standard Composition (Nested Subcomponents using Context)
 * <TitleTooltip title="Search Volume" tooltip="Average monthly search volume.">
 *   <TitleTooltip.Text>
 *     <TitleTooltip.Tooltip />
 *   </TitleTooltip.Text>
 * </TitleTooltip>
 * 
 * @example
 * // Option 3: Customized Composition (Explicit Props)
 * <TitleTooltip>
 *   <TitleTooltip.Text title="Custom Title" className="text-secondary">
 *     <TitleTooltip.Tooltip tooltip="Explicit tooltip content" />
 *   </TitleTooltip.Text>
 * </TitleTooltip>
 * 
 * @example
 * // Option 4: Custom Trigger Element inside the Tooltip
 * <TitleTooltip title="Domain Authority">
 *   <TitleTooltip.Text>
 *     <TitleTooltip.Tooltip tooltip="Learn more about DA">
 *       <span className="text-xs text-blue-500 underline cursor-pointer">(What is this?)</span>
 *     </TitleTooltip.Tooltip>
 *   </TitleTooltip.Text>
 * </TitleTooltip>
 */
function TitleTooltip(props: ITitleTooltipProps) {
  const { title, tooltip, children } = props;

  const contextValue = useMemo(() => ({
    title,
    tooltip
  }), [title, tooltip]);

  return (
    <TitleTooltipContext.Provider value={contextValue}>
      {children ? (
        children
      ) : (
        // Default/Legacy rendering mode if no children are provided
        <TitleTooltip.Text>
          {tooltip && <TitleTooltip.Tooltip />}
        </TitleTooltip.Text>
      )}
    </TitleTooltipContext.Provider>
  );
}

// 4. Subcomponents Implementation
/**
 * Text subcomponent for TitleTooltip.
 * Renders the title element. Automatically appends any child components (like TitleTooltip.Tooltip)
 * within an aligned inline container.
 */
TitleTooltip.Text = function TitleTooltipText({ children, title, className = '' }: ITitleTooltipTextProps) {
  const context = useTitleTooltipContext();
  const displayTitle = title ?? context.title;
  return (
    <p className={`text-primary text-[#5D5F6C] ${className}`}>
      {displayTitle}
      {children && (
        <span className="inline-flex align-middle ml-1">
          {children}
        </span>
      )}
    </p>
  );
};

/**
 * Tooltip subcomponent for TitleTooltip.
 * Renders a CustomTooltip. If children are provided, they act as the tooltip trigger;
 * otherwise, a default styled HelpIcon is rendered.
 */
TitleTooltip.Tooltip = function TitleTooltipTooltip({ children, tooltip, className = '' }: ITitleTooltipTooltipProps) {
  const context = useTitleTooltipContext();
  const displayTooltip = tooltip ?? context.tooltip;

  if (!displayTooltip) return null;

  const trigger = children ? (
    <span className={`inline-flex items-center align-middle`}>{children}</span>
  ) : (
    <HelpIcon sx={searchProgressSx?.helpIcon} />
  );

  return (
    <CustomTooltip title={<Box className={className}>{displayTooltip}</Box>} placement="bottom" arrow>
      {trigger}
    </CustomTooltip>
  );
};

export default TitleTooltip;
